/**
 * Animated star field overlay for magical atmosphere
 * Story 8.7: Creates subtle twinkling stars in twilight sky
 *
 * Design notes (from mood-story-sagamoon.md):
 * - Very subtle, not distracting (10-15% opacity)
 * - Gentle twinkle animation
 * - Should feel like a bedtime sky, not a planetarium
 * - Respects prefers-reduced-motion
 *
 * @module components/decorative/StarField
 */

import { useMemo } from 'react';

interface Star {
  id: number;
  cx: number;
  cy: number;
  r: number;
  animationDelay: string;
  animationClass: string;
}

interface StarFieldProps {
  /** Number of stars to render (default: 50) */
  starCount?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Generates deterministic star positions based on index
 * Uses simple hash to create consistent but varied positions
 */
function generateStars(count: number): Star[] {
  const stars: Star[] = [];
  const animationClasses = ['animate-twinkle', 'animate-twinkle-slow', 'animate-twinkle-fast'];

  for (let i = 0; i < count; i++) {
    // Pseudo-random but deterministic positioning
    const hash1 = (i * 7919) % 100;
    const hash2 = (i * 104729) % 100;
    const hash3 = (i * 15485863) % 3;
    const hash4 = (i * 32452843) % 5;

    stars.push({
      id: i,
      cx: hash1,
      cy: hash2,
      r: 0.5 + hash4 * 0.3, // 0.5-2px radius
      animationDelay: `${(i % 10) * 0.3}s`,
      animationClass: animationClasses[hash3],
    });
  }

  return stars;
}

/**
 * StarField component
 * Renders an animated field of twinkling stars
 * Fixed position overlay that covers the viewport
 */
export function StarField({ starCount = 50, className = '' }: StarFieldProps): JSX.Element {
  // Memoize star generation to avoid recalculating on every render
  const stars = useMemo(() => generateStars(starCount), [starCount]);

  return (
    <div
      className={`fixed inset-0 pointer-events-none overflow-hidden ${className}`}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
        className="w-full h-full opacity-[0.15]"
      >
        {/* Star field - subtle white/cream stars */}
        {stars.map((star) => (
          <circle
            key={star.id}
            cx={star.cx}
            cy={star.cy}
            r={star.r}
            fill="#FFF8F0"
            className={`${star.animationClass} motion-reduce:animate-none`}
            style={{ animationDelay: star.animationDelay }}
          />
        ))}
      </svg>
    </div>
  );
}
